import React, { useCallback, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import ImageViewer from 'react-simple-image-viewer';
import { Typography, Container } from '@mui/material';
import { styled } from '@mui/system';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import Chip from 'components/Chip';
import GradientHeading from 'components/GradientHeading';

const images = require.context('../images', false, /image-\d+\.webp$/);
const mock = Array.from({ length: 24 }, (_, i) =>
    images(`./image-${i + 1}.webp`),
);

const StyledContainer = styled(Container)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
    borderRadius: theme.spacing(3),
    padding: theme.spacing(6, 4),
    marginBottom: theme.spacing(4),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(45deg, ${theme.palette.primary.main}08 0%, transparent 50%, ${theme.palette.secondary.main}08 100%)`,
        pointerEvents: 'none',
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    position: 'relative',
    background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        '& .image-overlay': {
            opacity: 1,
        },
    },
}));

const ImageOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
        'linear-gradient(45deg, rgba(255,144,46,0.8) 0%, rgba(255,144,46,0.6) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    color: 'white',
    fontSize: '1.2rem',
    fontWeight: 600,
}));

const Main = () => {
    const [currentImage, setCurrentImage] = useState(0);
    const [isViewerOpen, setIsViewerOpen] = useState(false);

    const openImageViewer = useCallback((index) => {
        setCurrentImage(index);
        setIsViewerOpen(true);
    }, []);

    const closeImageViewer = () => {
        setCurrentImage(0);
        setIsViewerOpen(false);
    };

    const theme = useTheme();

    return (
        <Box sx={{ py: 4 }}>
            <StyledContainer maxWidth="lg">
                <Box textAlign="center" position="relative" zIndex={1}>
                    <Chip
                        icon={<PhotoLibraryIcon color="white" />}
                        label="GALLERY COLLECTION"
                        sx={{ mb: 3 }}
                    />
                    <GradientHeading label="INAACC Manado 2024" />
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.palette.text.secondary,
                            maxWidth: 600,
                            mx: 'auto',
                            lineHeight: 1.6,
                            fontWeight: 400,
                        }}
                    >
                        Explore our collection of memorable moments and
                        achievements
                    </Typography>
                </Box>
            </StyledContainer>

            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {mock.map((item, i) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                            <StyledCard onClick={() => openImageViewer(i)}>
                                <CardMedia
                                    image={item}
                                    title={`Gallery Image ${i + 1}`}
                                    sx={{
                                        height: { xs: 280, sm: 320, md: 360 },
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        filter:
                                            theme.palette.mode === 'dark'
                                                ? 'brightness(0.8)'
                                                : 'none',
                                    }}
                                />
                                <ImageOverlay className="image-overlay">
                                    <PhotoLibraryIcon sx={{ mr: 1 }} />
                                    View Image
                                </ImageOverlay>
                            </StyledCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {isViewerOpen && (
                <ImageViewer
                    src={mock}
                    currentIndex={currentImage}
                    disableScroll={false}
                    closeOnClickOutside={true}
                    onClose={closeImageViewer}
                    backgroundStyle={{
                        marginTop: 60,
                        background: 'rgba(0, 0, 0, 0.9)',
                        backdropFilter: 'blur(10px)',
                    }}
                />
            )}
        </Box>
    );
};

export default Main;
